import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useUIStore } from '@/store/uiStore';
import Sidebar from '@/components/common/Sidebar';
import TrialBanner from '@/components/common/TrialBanner';
import CameraScannerModal from '@/components/pos/CameraScannerModal';
import { posApi } from '@/services/api';
import { POSItem, ItemVariant, BarcodeScanResult } from '@/types';
import { formatMoney, getCurrencySymbol } from '@/utils/currency';
import { 
  Barcode, Search, X, Loader2, Layers, 
  AlertCircle, Menu, Info, CornerDownRight, CheckCircle2, ChevronRight
} from 'lucide-react';

export default function PriceCheckerPage() {
  const { t } = useTranslation();
  const { toggleSidebar } = useUIStore();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [scanPending, setScanPending] = useState(false);
  const [scanError, setScanError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const [results, setResults] = useState<POSItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<POSItem | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<ItemVariant | null>(null);
  
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Auto-focus search input on mount and scanner close
  const focusInput = () => {
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  };

  useEffect(() => {
    focusInput();
  }, []);

  const handleCloseScanner = () => {
    setShowScanner(false);
    focusInput();
  };

  // Perform barcode scan resolution
  const handleBarcodeLookup = useCallback(async (code: string): Promise<boolean> => {
    if (!code) return false;
    setScanPending(true);
    setScanError('');
    setSelectedItem(null);
    setSelectedVariant(null);
    setResults([]);

    try {
      const res = await posApi.scanBarcode(code);
      const result = res.data.message as BarcodeScanResult;
      
      // Fetch parent details to show full context, category, variants
      const parentRes = await posApi.getItems({ search: result.item_code || result.item, limit: 5 });
      const parentItems = parentRes.data.message.items as POSItem[];
      const parentItem = parentItems.find(i => i.name === result.item || i.item_code === result.item || i.item_code === result.item_code) || parentItems[0];

      if (parentItem) {
        setSelectedItem(parentItem);
        if (result.variant) {
          const matchedVariant = parentItem.variants?.find(v => v.name === result.variant);
          setSelectedVariant(matchedVariant || null);
        } else {
          setSelectedVariant(null);
        }
      } else {
        // Fallback when parent is not found in search results
        const mockItem: POSItem = {
          name: result.item,
          item_code: result.item_code || result.item,
          item_name: result.item_name,
          category: result.category || 'N/A',
          item_type: result.item_type,
          sale_price: result.rate,
          stock_quantity: result.stock_quantity,
          barcode: result.barcode,
          has_variants: false,
          is_active: true
        };
        setSelectedItem(mockItem);
        setSelectedVariant(null);
      }
      return true;
    } catch (err: any) {
      console.error("Barcode lookup failed:", err);
      const msg = err.response?.data?.message || 'Product barcode not found';
      setScanError(typeof msg === 'string' ? msg : 'Product not found');
      return false;
    } finally {
      setScanPending(false);
    }
  }, []);

  // Text search handler
  const handleSearchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = searchQuery.trim();
    if (!trimmed) return;

    if (trimmed.length < 3) {
      setScanError('Search query must be at least 3 characters.');
      return;
    }

    setLoading(true);
    setScanError('');
    setSelectedItem(null);
    setSelectedVariant(null);
    setResults([]);

    try {
      // 1. Try barcode lookup first
      const barcodeRes = await posApi.scanBarcode(trimmed);
      const result = barcodeRes.data.message as BarcodeScanResult;
      
      const parentRes = await posApi.getItems({ search: result.item_code || result.item, limit: 5 });
      const parentItems = parentRes.data.message.items as POSItem[];
      const parentItem = parentItems.find(i => i.name === result.item || i.item_code === result.item || i.item_code === result.item_code) || parentItems[0];

      if (parentItem) {
        setSelectedItem(parentItem);
        if (result.variant) {
          const matchedVariant = parentItem.variants?.find(v => v.name === result.variant);
          setSelectedVariant(matchedVariant || null);
        }
      } else {
        const mockItem: POSItem = {
          name: result.item,
          item_code: result.item_code || result.item,
          item_name: result.item_name,
          category: result.category || 'N/A',
          item_type: result.item_type,
          sale_price: result.rate,
          stock_quantity: result.stock_quantity,
          barcode: result.barcode,
          has_variants: false,
          is_active: true
        };
        setSelectedItem(mockItem);
      }
    } catch (err) {
      // 2. Fallback to normal text search
      try {
        const res = await posApi.getItems({ search: trimmed, limit: 30 });
        const items = res.data.message.items as POSItem[];
        setResults(items);
        if (items.length === 1) {
          setSelectedItem(items[0]);
        } else if (items.length === 0) {
          setScanError(`No products found matching "${trimmed}"`);
        }
      } catch (searchErr: any) {
        console.error("General items search failed:", searchErr);
        const msg = searchErr.response?.data?.message || 'Error searching for product';
        setScanError(typeof msg === 'string' ? msg : 'Search failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSearchQuery('');
    setScanError('');
    setSelectedItem(null);
    setSelectedVariant(null);
    setResults([]);
    focusInput();
  };

  const selectResultItem = (item: POSItem) => {
    setSelectedItem(item);
    setSelectedVariant(null);
    setResults([]);
  };

  // Helper to determine stock badge styles
  const getStockBadge = (item: POSItem, variant?: ItemVariant | null) => {
    if (item.item_type !== 'Product') {
      return {
        label: t('priceChecker.service'),
        bg: 'bg-indigo-50 text-indigo-700 border-indigo-200',
        qty: null
      };
    }

    const qty = variant ? (variant.stock_quantity ?? 0) : (item.stock_quantity ?? 0);

    if (qty <= 0) {
      return {
        label: t('priceChecker.outOfStock'),
        bg: 'bg-red-50 text-red-700 border-red-200',
        qty
      };
    } else if (qty <= 5) {
      return {
        label: t('priceChecker.lowStock'),
        bg: 'bg-amber-50 text-amber-700 border-amber-200',
        qty
      };
    } else {
      return {
        label: t('priceChecker.inStock'),
        bg: 'bg-emerald-50 text-emerald-700 border-emerald-200',
        qty
      };
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-auto flex flex-col">
        <TrialBanner />
        
        {/* Main Content Area */}
        <div className="p-6 max-w-4xl w-full mx-auto flex-1 flex flex-col min-h-0">
          
          {/* Header */}
          <div className="flex items-center gap-3 mb-6 shrink-0">
            <button onClick={toggleSidebar} className="lg:hidden p-2 -ml-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg shrink-0">
              <Menu className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Barcode className="w-6 h-6 text-primary-600" /> {t('priceChecker.title')}
              </h1>
              <p className="text-gray-500 text-sm">{t('priceChecker.subtitle')}</p>
            </div>
          </div>

          {/* Search Controls */}
          <div className="card p-4 mb-6 shrink-0 shadow-md border-primary-100 bg-white/80 backdrop-blur-sm">
            <form onSubmit={handleSearchSubmit} className="flex gap-2">
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder={t('priceChecker.placeholder')}
                  className="input-field pl-10 pr-10 py-3 text-base w-full focus:ring-primary-500"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  disabled={loading || scanPending}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>
              
              <button
                type="submit"
                className="btn-primary px-6 flex items-center gap-2 text-base font-semibold shadow-sm"
                disabled={loading || scanPending}
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('common.search')}
              </button>

              <button
                type="button"
                onClick={() => setShowScanner(true)}
                className="flex items-center justify-center p-3 rounded-lg border-2 border-primary-500 text-primary-600 bg-primary-50 hover:bg-primary-100 hover:border-primary-600 transition-all font-semibold"
                title={t('priceChecker.scanCamera')}
                disabled={loading || scanPending}
              >
                <Barcode className="w-6 h-6" />
              </button>
            </form>
          </div>

          {/* Messages & Alerts */}
          {scanError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3 text-red-800 animate-in fade-in shrink-0">
              <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-sm">{t('priceChecker.searchFailed')}</p>
                <p className="text-xs text-red-700 mt-0.5">{scanError}</p>
              </div>
            </div>
          )}

          {/* Results Container */}
          <div className="flex-1 min-h-0 overflow-auto pb-8">
            
            {/* Loading Indicator */}
            {loading && (
              <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl shadow-sm border border-gray-100">
                <Loader2 className="w-12 h-12 text-primary-500 animate-spin" />
                <p className="text-gray-500 text-sm mt-3 font-medium">{t('priceChecker.fetching')}</p>
              </div>
            )}

            {/* Default Empty State */}
            {!loading && !selectedItem && results.length === 0 && !scanError && (
              <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-dashed border-gray-200 text-center px-4">
                <div className="w-16 h-16 bg-primary-50 text-primary-600 rounded-full flex items-center justify-center mb-4">
                  <Barcode className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">{t('priceChecker.emptyTitle')}</h3>
                <p className="text-gray-500 text-sm max-w-sm">
                  {t('priceChecker.emptyHelp')}
                </p>
              </div>
            )}

            {/* List of Multiple Matches */}
            {!loading && results.length > 1 && (
              <div className="space-y-3 animate-in fade-in">
                <div className="flex items-center justify-between text-xs text-gray-500 px-1">
                  <span>{t('priceChecker.foundMatches', { count: results.length })}</span>
                  <span>{t('priceChecker.selectOne')}</span>
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {results.map((item) => (
                    <div
                      key={item.name}
                      onClick={() => selectResultItem(item)}
                      className="card p-4 flex items-center justify-between hover:border-primary-400 hover:shadow-md transition-all cursor-pointer bg-white group"
                    >
                      <div className="min-w-0 pr-3">
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="text-xs font-semibold px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                            {item.category}
                          </span>
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${
                            item.item_type === 'Product' ? 'bg-emerald-50 border-emerald-100 text-emerald-700' : 'bg-indigo-50 border-indigo-100 text-indigo-700'
                          }`}>
                            {item.item_type}
                          </span>
                        </div>
                        <h4 className="font-bold text-gray-900 truncate group-hover:text-primary-600 transition-colors">
                          {item.item_name}
                        </h4>
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                          {t('priceChecker.code')} {item.item_code} {item.barcode && `| ${t('priceChecker.barcode')} ${item.barcode}`}
                        </p>
                      </div>
                      <div className="text-right shrink-0 flex items-center gap-2">
                        <div>
                          <p className="text-sm font-black text-gray-900">
                            {formatMoney((item.sale_price || 0))}
                          </p>
                          {item.item_type === 'Product' && (
                            <p className="text-[10px] text-gray-500 mt-0.5">
                              {t('priceChecker.stock')} {item.stock_quantity ?? 0} {item.unit_of_measurement || t('priceChecker.units')}
                            </p>
                          )}
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-primary-500 transition-colors" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Detailed View Card */}
            {!loading && selectedItem && (
              <div className="space-y-6 animate-in slide-in-from-bottom-4 duration-300">
                
                {/* Main Product Card */}
                <div className="card overflow-hidden bg-white shadow-lg border-primary-50">
                  {/* Card Header Banner */}
                  <div className="bg-primary-900 text-white p-6 relative">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className="text-xs font-bold bg-white/20 px-2.5 py-1 rounded-full uppercase tracking-wider">
                        {selectedItem.category}
                      </span>
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full border border-white/30 uppercase tracking-wider ${
                        selectedItem.item_type === 'Product' ? 'bg-emerald-500/20 text-emerald-100' : 'bg-indigo-500/20 text-indigo-100'
                      }`}>
                        {selectedItem.item_type}
                      </span>
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-black tracking-tight leading-tight">
                      {selectedItem.item_name}
                    </h2>
                    <p className="text-white/60 text-xs sm:text-sm mt-1 font-mono">
                      {t('priceChecker.id')} {selectedItem.item_code} {selectedItem.barcode && `• ${t('priceChecker.barcode')} ${selectedItem.barcode}`}
                    </p>
                  </div>

                  {/* Pricing and Stock Info */}
                  <div className="p-6">
                    <div className="grid gap-6 sm:grid-cols-2">
                      {/* Price Details */}
                      <div className="bg-emerald-50/50 border border-emerald-100 rounded-2xl p-5 flex flex-col justify-between">
                        <div>
                          <span className="text-xs font-bold text-emerald-800 uppercase tracking-wider">
                            {t('priceChecker.sellingPrice')}
                          </span>
                          <div className="text-3xl sm:text-4xl font-black text-emerald-700 mt-2 flex items-baseline gap-1">
                            <span className="text-lg font-bold">{getCurrencySymbol()}</span>
                            <span>
                              {selectedVariant 
                                ? (selectedVariant.price || 0).toLocaleString()
                                : (selectedItem.sale_price || 0).toLocaleString()
                              }
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-emerald-700 font-semibold mt-4">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          <span>{t('priceChecker.includesTax')}</span>
                        </div>
                      </div>

                      {/* Stock Details */}
                      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 flex flex-col justify-between">
                        <div>
                          <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                            {t('priceChecker.availability')}
                          </span>
                          <div className="mt-3">
                            {(() => {
                              const badge = getStockBadge(selectedItem, selectedVariant);
                              return (
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className={`inline-block px-3 py-1 text-xs font-bold rounded-full border ${badge.bg}`}>
                                      {badge.label}
                                    </span>
                                  </div>
                                  {badge.qty !== null && (
                                    <div className="text-3xl font-black text-gray-800 mt-3">
                                      {badge.qty} <span className="text-sm font-semibold text-gray-500">{selectedItem.unit_of_measurement || t('priceChecker.units')}</span>
                                    </div>
                                  )}
                                  {badge.qty === null && (
                                    <div className="text-sm text-gray-500 mt-3 font-semibold">
                                      {t('priceChecker.unlimitedService')}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-4">
                          <Info className="w-4 h-4 text-gray-400" />
                          <span>{t('priceChecker.stockHint')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Variants Price Cards */}
                {!!selectedItem.has_variants && selectedItem.variants && selectedItem.variants.length > 0 && (
                  <div className="space-y-3 animate-in fade-in">
                    <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wider flex items-center gap-1.5 px-1">
                      <Layers className="w-4 h-4 text-primary-500" /> {t('priceChecker.variantPrices')}
                    </h3>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {selectedItem.variants.map((v) => {
                        const isScannedOption = selectedVariant?.name === v.name;
                        const vBadge = getStockBadge(selectedItem, v);
                        return (
                          <div
                            key={v.name}
                            className={`card p-4 transition-all relative ${
                              isScannedOption
                                ? 'border-primary-500 bg-primary-50/20 ring-2 ring-primary-500/20 shadow-md'
                                : 'border-gray-200 hover:border-gray-300 bg-white'
                            }`}
                          >
                            {isScannedOption && (
                              <span className="absolute top-3 right-3 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-primary-600 text-white rounded-full">
                                {t('priceChecker.scannedOption')}
                              </span>
                            )}
                            
                            <h4 className="font-bold text-gray-900 text-base flex items-center gap-1">
                              <CornerDownRight className="w-4 h-4 text-gray-400 shrink-0" />
                              {v.variant_name}
                            </h4>
                            <p className="text-xs text-gray-400 font-mono pl-5 mt-0.5">
                              {t('priceChecker.variantCode')} {v.name} {v.barcode && `| ${t('priceChecker.barcode')} ${v.barcode}`}
                            </p>

                            <div className="flex items-end justify-between pl-5 mt-4">
                              <div>
                                <span className="text-[10px] font-bold text-gray-400 uppercase">{t('common.price')}</span>
                                <p className="text-lg font-black text-gray-900 leading-none mt-1">
                                  {formatMoney((v.price || 0))}
                                </p>
                              </div>
                              
                              {selectedItem.item_type === 'Product' && (
                                <div className="text-right">
                                  <span className="text-[10px] font-bold text-gray-400 uppercase">{t('priceChecker.stock').replace(':', '')}</span>
                                  <p className={`text-xs font-bold mt-1 px-2 py-0.5 rounded border inline-block ${vBadge.bg}`}>
                                    {v.stock_quantity ?? 0} {v.unit_of_measurement || selectedItem.unit_of_measurement || 'Qty'}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              </div>
            )}

          </div>
        </div>
      </div>

      {/* Camera Barcode Scanner Modal */}
      {showScanner && (
        <CameraScannerModal
          onScan={handleBarcodeLookup}
          onClose={handleCloseScanner}
          scanPending={scanPending}
          scanError={scanError}
        />
      )}
    </div>
  );
}
